import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie, Check, X } from "lucide-react";
import { getConsent, saveConsent, loadTracking } from "@/lib/tracking";

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [choice, setChoice] = useState({ statistics: false, marketing: false, external_media: false });

  useEffect(() => {
    const c = getConsent();
    if (!c) {
      const t = setTimeout(() => setVisible(true), 700);
      return () => clearTimeout(t);
    }
    // consent exists → make sure tracking is loaded
    loadTracking();
  }, []);

  const accept = (all) => {
    const c = all
      ? { statistics: true, marketing: true, external_media: true }
      : choice;
    saveConsent(c);
    loadTracking();
    setVisible(false);
  };

  const rejectAll = () => {
    saveConsent({ statistics: false, marketing: false, external_media: false });
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed z-[70] left-3 right-3 md:left-6 md:right-auto md:bottom-6 bottom-20 md:max-w-lg"
          data-testid="cookie-banner"
        >
          <div className="glass-strong rounded-2xl p-5 border border-white/15 shadow-2xl">
            {!showSettings ? (
              <>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[#E8E5D0]/15 border border-[#E8E5D0]/30 flex items-center justify-center shrink-0">
                    <Cookie size={18} className="text-[#E8E5D0]" />
                  </div>
                  <div className="text-sm text-[#E5E2D0] leading-relaxed">
                    Wir verwenden Cookies für Funktion, Statistik und Marketing. Notwendige Cookies sind
                    immer aktiv. Details in unserer{" "}
                    <Link to="/datenschutz" className="text-[#E8E5D0] hover:underline">Datenschutzerklärung</Link>.
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={rejectAll} className="text-xs font-bold uppercase tracking-wider border border-white/15 hover:bg-black/[0.15] rounded-full px-4 py-2.5" data-testid="cookie-reject">
                    Nur notwendige
                  </button>
                  <button onClick={() => setShowSettings(true)} className="text-xs font-bold uppercase tracking-wider border border-white/15 hover:bg-black/[0.15] rounded-full px-4 py-2.5" data-testid="cookie-settings">
                    Einstellungen
                  </button>
                  <button onClick={() => accept(true)} className="flex-1 text-xs font-extrabold uppercase tracking-wider bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] rounded-full px-4 py-2.5" data-testid="cookie-accept">
                    Alle akzeptieren
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-display font-black text-[#F3F1E8]">Cookie-Einstellungen</div>
                  <button onClick={() => setShowSettings(false)} className="text-[#C9C6B4]"><X size={16}/></button>
                </div>
                <div className="space-y-2 text-sm mb-3">
                  {[
                    { key: "necessary", label: "Notwendig", desc: "Grundfunktionen der Website. Immer aktiv.", locked: true },
                    { key: "statistics", label: "Statistik", desc: "Anonyme Nutzungsanalyse (z. B. Google Analytics)." },
                    { key: "marketing", label: "Marketing", desc: "Werbeanzeigen & Remarketing (z. B. Meta, Google Ads)." },
                    { key: "external_media", label: "Externe Medien", desc: "YouTube, Vimeo, Instagram-Einbettungen." },
                  ].map((cat) => (
                    <label key={cat.key} className="flex items-start gap-3 p-2 bg-black/20 rounded-lg border border-white/8">
                      <input
                        type="checkbox"
                        checked={cat.locked ? true : !!choice[cat.key]}
                        disabled={cat.locked}
                        onChange={(e) => setChoice({ ...choice, [cat.key]: e.target.checked })}
                        className="mt-0.5"
                        data-testid={`consent-${cat.key}`}
                      />
                      <div>
                        <div className="text-[#F3F1E8] font-bold text-xs">{cat.label}</div>
                        <div className="text-[11px] text-[#A8A58F]">{cat.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={rejectAll} className="flex-1 text-xs font-bold uppercase tracking-wider border border-white/15 hover:bg-black/[0.15] rounded-full px-4 py-2.5">
                    Nur notwendige
                  </button>
                  <button onClick={() => accept(false)} className="flex-1 text-xs font-extrabold uppercase tracking-wider bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] rounded-full px-4 py-2.5" data-testid="cookie-save-selection">
                    Auswahl speichern
                  </button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
